/**
 * Keeps React's commit phase alive when a node it owns has been moved or
 * replaced behind its back.
 *
 * React commits DOM changes against the nodes it recorded at render time:
 * `parent.removeChild(node)` and `parent.insertBefore(node, ref)`. Anything
 * that rewrites the live DOM outside React — browser page translation, a
 * content-injecting extension — re-parents or detaches those nodes, so the
 * next commit hands the browser a node that is no longer a child of `parent`
 * and the DOM throws `NotFoundError`. Thrown during commit, it unwinds to the
 * nearest boundary, and the only one is at the app root — so a single stale
 * text node replaces the whole page with the generic error fallback.
 *
 * ENG-4017 is that, and the same signature has hit `/login`, `/signup` and
 * `/settings/users` across six accounts — so it is not one feature's bug.
 *
 * What React is asking for is safe to complete either way: it wants a node it
 * owns gone, or a node it just built inserted. These wrappers do that against
 * the DOM as it actually is instead of throwing. Worst case a node lingers or
 * lands in the wrong position — a cosmetic glitch instead of a dead page.
 */
import * as Sentry from '@sentry/react';

/**
 * One event per session. It only needs to answer "is this still happening, and
 * what is rewriting the DOM" — a count per user would tell us nothing more and
 * this fires on every re-render of an affected subtree.
 */
let reported = false;
let installed = false;

/** `button.inline-flex` / `#text` — enough to locate it, not a whole class list. */
const describeNode = (node: Node | null): string => {
  if (!node) return 'detached';
  if (!(node instanceof Element)) return node.nodeName;
  const first =
    typeof node.className === 'string'
      ? node.className.trim().split(/\s+/)[0]
      : '';
  return `${node.nodeName.toLowerCase()}${first ? `.${first}` : ''}`;
};

/**
 * Names the rewriter when it leaves a trace: Chrome's translator stamps
 * `translated-ltr`/`translated-rtl` on `<html>`, Edge/Bing's marks what it
 * rewrote with `_msttexthash`. `unknown` is a useful answer too — it rules
 * translation out and points at an extension instead.
 */
const detectRewriter = (): string => {
  try {
    if (/\btranslated-(ltr|rtl)\b/.test(document.documentElement.className)) {
      return 'chrome-translate';
    }
    if (document.querySelector('font[_msttexthash]')) return 'edge-translate';
    return 'unknown';
  } catch {
    return 'unknown';
  }
};

const reportOnce = (
  operation: 'removeChild' | 'insertBefore',
  parent: Node,
  node: Node
): void => {
  if (reported) return;
  reported = true;

  Sentry.withScope((scope) => {
    scope.setTag('source', 'dom-mutation-guard');
    scope.setTag('dom_guard.rewriter', detectRewriter());
    scope.setContext('domMutationGuard', {
      operation,
      parent: describeNode(parent),
      node: describeNode(node),
      // Detached vs. re-parented separates "replaced outright" from "wrapped
      // in place" — the tell for which kind of rewriter is at work.
      actualParent: describeNode(node.parentNode),
    });
    Sentry.captureMessage(
      'DOM mutated outside React, commit recovered',
      'warning'
    );
  });
};

type RemoveChildFn = (this: Node, child: Node) => Node;
type InsertBeforeFn = (this: Node, node: Node, reference: Node | null) => Node;

/**
 * Cold path: reached only when the DOM would have thrown, or when the argument
 * was never a Node. Kept out of the wrapper so the hot path stays a compare,
 * and wrapped so a guard can never itself become a new source of crashes.
 */
const recoverRemoveChild = <T extends Node>(
  native: RemoveChildFn,
  parent: Node,
  child: T
): T => {
  // Not a Node at all — hand it straight back so the browser throws its own
  // TypeError, exactly as it would have unpatched.
  if (!(child instanceof Node)) return native.call(parent, child) as T;
  try {
    reportOnce('removeChild', parent, child);
    // Honour the intent: React wants this node gone. A translator wraps text
    // in place, leaving the original one level down — dropping it from its
    // real parent clears the stale copy the user can still see. Already
    // detached means there is nothing left to do.
    const actualParent = child.parentNode;
    return actualParent ? (native.call(actualParent, child) as T) : child;
  } catch {
    return child;
  }
};

const recoverInsertBefore = <T extends Node>(
  native: InsertBeforeFn,
  parent: Node,
  node: T,
  reference: Node
): T => {
  if (!(reference instanceof Node)) {
    return native.call(parent, node, reference) as T;
  }
  try {
    reportOnce('insertBefore', parent, reference);
    // The anchor is gone, so the position is unrecoverable. Appending keeps
    // the node in the tree; order is the only casualty.
    return native.call(parent, node, null) as T;
  } catch {
    return node;
  }
};

/**
 * Idempotent — safe under HMR and across test files. Call before the first
 * render, and after `Sentry.init` so the one report is actually sent.
 */
export const installDomMutationGuards = (): void => {
  if (installed) return;
  if (typeof Node === 'undefined' || !Node.prototype) return;
  installed = true;

  const nativeRemoveChild = Node.prototype.removeChild as RemoveChildFn;
  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(
    this: Node,
    child: T
  ): T {
    // Hot path is one property read and one compare — no `instanceof`, which
    // walks a prototype chain. Everything else, including a non-Node argument,
    // falls into the branch below and ends up back at the native call.
    if (child?.parentNode !== this) {
      return recoverRemoveChild(nativeRemoveChild, this, child);
    }
    return nativeRemoveChild.call(this, child) as T;
  };

  const nativeInsertBefore = Node.prototype.insertBefore as InsertBeforeFn;
  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(
    this: Node,
    node: T,
    reference: Node | null
  ): T {
    // A null/omitted reference is the append case and always valid. A Node is
    // never falsy, so this only skips arguments the native call would reject.
    if (reference && reference.parentNode !== this) {
      return recoverInsertBefore(nativeInsertBefore, this, node, reference);
    }
    return nativeInsertBefore.call(this, node, reference) as T;
  };
};
